/**
 * SourcingDashboardPage — funnel visibility for the cold-outreach pipeline.
 *
 * Route: /admin/sourcing
 * Auth: requires admin JWT (bounces to /admin/login if absent)
 *
 * Shows the funnel from sourced → onboarded with conversion rates, a 7-day
 * trend, and a recent-candidates table with current state. Pairs with the
 * gmail_reply_sweep cron + /api/admin/ops/sourcing/* endpoints.
 *
 * Built 2026-05-18 — Week 1 #4 of sourcing-engine hardening.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";
import PipelineStatusChevron from "@/components/admin/PipelineStatusChevron";

const FUNNEL_STAGES = [
  { key: "sourced",     label: "Sourced",      hint: "Apollo / GitHub / etc. discovered" },
  { key: "pb_enriched", label: "PB enriched",  hint: "Phantombuster scraped LinkedIn" },
  { key: "drafted",     label: "Drafted",      hint: "Gmail draft generated by Haiku" },
  { key: "sent",        label: "Sent",         hint: "Founder hit send" },
  { key: "replied",     label: "Replied",      hint: "Got any reply at all" },
  { key: "interested",  label: "Interested",   hint: "Reply classified as interested" },
  { key: "onboarded",   label: "Onboarded",    hint: "Now an active expert" },
];

const STATUS_COLORS = {
  interested:      "bg-emerald-100 text-emerald-800",
  declined:        "bg-rose-50 text-rose-700",
  needs_info:      "bg-amber-50 text-amber-800",
  unsubscribe:     "bg-zinc-200 text-zinc-700",
  bounced:         "bg-zinc-200 text-zinc-700",
  awaiting:        "bg-sky-50 text-sky-700",
  no_response_30d: "bg-zinc-100 text-zinc-600",
};

function authHeaders() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function pct(n) {
  return n == null ? "—" : `${Math.round(n * 100)}%`;
}

export default function SourcingDashboardPage() {
  const navigate = useNavigate();
  const [windowDays, setWindowDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [f, r] = await Promise.all([
        axios.get(`${API}/admin/ops/sourcing/funnel?days=${windowDays}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/ops/sourcing/recent?limit=50`, { headers: authHeaders() }),
      ]);
      setFunnel(f.data);
      setRecent(r.data.candidates || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/admin/login");
        return;
      }
      setErr(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [windowDays, navigate]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF8F4] text-[#0E1410]">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header — italic-green accent per brand rule */}
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-[#17803D] mb-3">
            SOURCING / FUNNEL
          </p>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight">
            What's <em className="text-[#17803D] not-italic-fallback italic">actually working</em> in outreach
          </h1>
          <p className="mt-3 text-sm text-zinc-600 max-w-2xl">
            Counts and conversion rates across the cold-outreach funnel, last{" "}
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="bg-transparent border-b border-zinc-300 px-1 focus:outline-none focus:border-[#17803D]"
            >
              <option value={7}>7</option>
              <option value={14}>14</option>
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>{" "}
            days. Refreshes on tab focus.
          </p>
        </header>

        {err && (
          <div className="mb-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {err}
          </div>
        )}

        {/* Live pipeline-stage tracker — polls every 30s */}
        <PipelineStatusChevron />

        {/* Funnel cards */}
        <section className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-10">
          {FUNNEL_STAGES.map((s) => {
            const v = funnel?.totals?.[s.key];
            return (
              <div key={s.key} className="rounded-md border border-zinc-200 bg-white px-4 py-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500">{s.label}</div>
                <div className="font-serif text-3xl mt-1 text-[#0E1410]">
                  {loading ? "…" : (v ?? 0)}
                </div>
                <div className="text-[10px] text-zinc-400 mt-1 leading-tight">{s.hint}</div>
              </div>
            );
          })}
        </section>

        {/* Conversion rates */}
        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-4">Conversion rates</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["PB enrichment", funnel?.conversion_rates?.pb_enrichment],
              ["Draft created", funnel?.conversion_rates?.draft],
              ["Sent",          funnel?.conversion_rates?.send],
              ["Got reply",     funnel?.conversion_rates?.reply],
              ["Interest",      funnel?.conversion_rates?.interest],
            ].map(([label, val]) => (
              <div key={label} className="rounded-md border border-zinc-200 bg-white px-4 py-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
                <div className="font-mono text-2xl mt-1">{pct(val)}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Trend */}
        {funnel?.trend_7d?.length > 0 && (
          <section className="mb-10">
            <h2 className="font-serif text-2xl mb-4">Last 7 days</h2>
            <div className="rounded-md border border-zinc-200 bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-right">Sourced</th>
                    <th className="px-4 py-2 text-right">Drafted</th>
                    <th className="px-4 py-2 text-right">Sent</th>
                    <th className="px-4 py-2 text-right">Replied</th>
                  </tr>
                </thead>
                <tbody>
                  {funnel.trend_7d.map((row) => (
                    <tr key={row.date} className="border-t border-zinc-100">
                      <td className="px-4 py-2 font-mono">{row.date}</td>
                      <td className="px-4 py-2 text-right">{row.sourced}</td>
                      <td className="px-4 py-2 text-right">{row.drafted}</td>
                      <td className="px-4 py-2 text-right">{row.sent}</td>
                      <td className="px-4 py-2 text-right">{row.replied}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Recent candidates */}
        <section className="mb-12">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-serif text-2xl">Recent candidates</h2>
            <button
              onClick={load}
              className="text-xs text-[#17803D] hover:underline"
            >
              Refresh
            </button>
          </div>
          <div className="rounded-md border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Company</th>
                  <th className="px-4 py-2 text-left">Category</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-left">PB</th>
                  <th className="px-4 py-2 text-left">Draft</th>
                  <th className="px-4 py-2 text-left">Reply</th>
                  <th className="px-4 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => {
                  const statusCls = STATUS_COLORS[c.outreach_reply_status] || "bg-zinc-50 text-zinc-500";
                  return (
                    <tr key={c.entity_hash} className="border-t border-zinc-100">
                      <td className="px-4 py-2">{c.full_name || "—"}</td>
                      <td className="px-4 py-2 text-zinc-600 max-w-[200px] truncate" title={c.current_employer}>
                        {c.current_employer || "—"}
                      </td>
                      <td className="px-4 py-2 text-zinc-500 text-xs">{c.sourcing_category || "—"}</td>
                      <td className="px-4 py-2 text-zinc-500 text-xs">{c.source || "apollo"}</td>
                      <td className="px-4 py-2">
                        {c.pb_enrichment_status === "success" ? (
                          <span className="text-[#17803D]">✓</span>
                        ) : c.pb_enrichment_status === "failed" ? (
                          <span className="text-rose-500">✗</span>
                        ) : (
                          <span className="text-zinc-300">·</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-zinc-500 text-xs">
                        {c.gmail_drafted_at ? "drafted" : "—"}
                        {c.outreach_sent_at ? " · sent" : ""}
                      </td>
                      <td className="px-4 py-2">
                        {c.outreach_reply_status ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${statusCls}`}>
                            {c.outreach_reply_status}
                          </span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-zinc-400 text-xs font-mono">
                        {c.created_at ? c.created_at.slice(0, 10) : "—"}
                      </td>
                    </tr>
                  );
                })}
                {recent.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-zinc-400">
                      No candidates yet in this window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
